import { supabase } from '../lib/supabase'

const cursoColumns = 'id, organizacion_id, anio, nombre, nivel, seccion, activo, created_at, updated_at'

function requireOrganization(organizacionId) {
  if (!organizacionId) throw new Error('No fue posible identificar la organización.')
}

function serviceError(error) {
  if (error?.code === '23505') return new Error('Este curso ya está asociado al socio.')
  return new Error('No fue posible completar la operación. Intenta nuevamente.')
}

export async function obtenerCursosDeSocio(organizacionId, socioId) {
  requireOrganization(organizacionId)
  const { data: relations, error: relationError } = await supabase
    .from('cp_socio_cursos')
    .select('id, organizacion_id, socio_id, curso_id, created_at')
    .eq('organizacion_id', organizacionId)
    .eq('socio_id', socioId)

  if (relationError) throw serviceError(relationError)
  if (!relations?.length) return []

  const courseIds = relations.map((relation) => relation.curso_id)
  const { data: courses, error: courseError } = await supabase
    .from('cp_cursos')
    .select(cursoColumns)
    .eq('organizacion_id', organizacionId)
    .in('id', courseIds)
    .order('anio', { ascending: false })
    .order('nombre', { ascending: true })

  if (courseError) throw serviceError(courseError)
  const relationByCourse = new Map(relations.map((relation) => [relation.curso_id, relation]))
  return (courses ?? []).map((course) => ({ ...course, relacion_id: relationByCourse.get(course.id)?.id }))
}

export async function obtenerCursosActivosParaAsociar(organizacionId) {
  requireOrganization(organizacionId)
  const { data, error } = await supabase
    .from('cp_cursos')
    .select(cursoColumns)
    .eq('organizacion_id', organizacionId)
    .eq('activo', true)
    .order('anio', { ascending: false })
    .order('nombre', { ascending: true })

  if (error) throw serviceError(error)
  return data ?? []
}

export async function obtenerConteosCursosPorSocio(organizacionId, socioIds) {
  requireOrganization(organizacionId)
  const uniqueIds = [...new Set(socioIds.filter(Boolean))]
  if (!uniqueIds.length) return {}

  const { data, error } = await supabase
    .from('cp_socio_cursos')
    .select('socio_id')
    .eq('organizacion_id', organizacionId)
    .in('socio_id', uniqueIds)

  if (error) throw serviceError(error)
  return (data ?? []).reduce((counts, relation) => {
    counts[relation.socio_id] = (counts[relation.socio_id] ?? 0) + 1
    return counts
  }, {})
}

export async function obtenerConteosSociosPorCurso(organizacionId, cursoIds) {
  requireOrganization(organizacionId)
  const uniqueIds = [...new Set(cursoIds.filter(Boolean))]
  if (!uniqueIds.length) return {}

  const { data, error } = await supabase
    .from('cp_socio_cursos')
    .select('curso_id')
    .eq('organizacion_id', organizacionId)
    .in('curso_id', uniqueIds)

  if (error) throw serviceError(error)
  return (data ?? []).reduce((counts, relation) => {
    counts[relation.curso_id] = (counts[relation.curso_id] ?? 0) + 1
    return counts
  }, {})
}

export async function asociarCursosSocio(organizacionId, socioId, cursoIds) {
  requireOrganization(organizacionId)
  const uniqueCourseIds = [...new Set(cursoIds.filter(Boolean))]
  if (!uniqueCourseIds.length) return []

  const [{ data: socio, error: socioError }, { data: courses, error: courseError }] = await Promise.all([
    supabase.from('cp_socios').select('id').eq('organizacion_id', organizacionId).eq('id', socioId).maybeSingle(),
    supabase.from('cp_cursos').select('id').eq('organizacion_id', organizacionId).eq('activo', true).in('id', uniqueCourseIds),
  ])

  if (socioError || courseError) throw serviceError(socioError || courseError)
  if (!socio) throw new Error('Socio no encontrado.')
  if ((courses ?? []).length !== uniqueCourseIds.length) throw new Error('Uno o más cursos no están disponibles.')

  const rows = uniqueCourseIds.map((cursoId) => ({
    organizacion_id: organizacionId,
    socio_id: socioId,
    curso_id: cursoId,
  }))
  const { data, error } = await supabase
    .from('cp_socio_cursos')
    .insert(rows)
    .select('id, organizacion_id, socio_id, curso_id, created_at')

  if (error) throw serviceError(error)
  return data ?? []
}

export async function asociarCursoSocio(organizacionId, socioId, cursoId) {
  const relations = await asociarCursosSocio(organizacionId, socioId, [cursoId])
  return relations[0] ?? null
}

export async function quitarCursoSocio(organizacionId, socioId, cursoId) {
  requireOrganization(organizacionId)
  const { data, error } = await supabase
    .from('cp_socio_cursos')
    .delete()
    .eq('organizacion_id', organizacionId)
    .eq('socio_id', socioId)
    .eq('curso_id', cursoId)
    .select('id')
    .maybeSingle()

  if (error) throw serviceError(error)
  if (!data) throw new Error('La asociación no existe.')
  return data
}
